import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { money } from '../../ServiceDashboard/components/serviceCatalog';
import {
  checkedTasks,
  poolOf,
  scopeGroups,
  selectionPrice,
  tierAmount,
  tiersOf,
} from './quoteCatalog';
import {
  buildPick,
  fetchCatalogServices,
  saveBookingServicesForPicks,
} from '../../../utils/catalogPicks';

function taskCount(groups) {
  return (groups || []).reduce((sum, group) => sum + (group.tasks || []).length, 0);
}

function dollars(value) {
  return money(value) || '$0';
}

/** persist=false: only pick from the catalog (e.g. for an invoice); no booking service is created. */
export default function QuotationServicePicker({
  bookingId,
  onClose,
  onAdded,
  persist = true,
  zIndex,
  showInstructions = true,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState('');
  const [step, setStep] = useState('choose');
  const [tierId, setTierId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [checked, setChecked] = useState({});

  useEffect(() => {
    let cancel = false;
    fetchCatalogServices()
      .then((rows) => {
        if (!cancel) setServices(rows);
      })
      .catch((err) => {
        if (!cancel) toast.error(err?.message || 'Could not load services');
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, []);

  const service = services.find((row) => row.id === serviceId) || null;
  const tiers = tiersOf(service);
  const pool = poolOf(service);
  const tier = tiers.find((row) => row.id === tierId) || null;
  const groups = scopeGroups(service, tierId ? tier : null);
  const activeGroup = groups.find((row) => row.id === groupId) || groups[0] || null;
  const price = selectionPrice(tierId ? tier : null, groups, checked);
  const usingTierPrice = Boolean(tier && tierAmount(tier) > 0);
  const code = tier?.code || service?.serviceId || '';

  const seedChecks = (row, nextTier) => {
    const next = {};
    scopeGroups(row, nextTier).forEach((group) => {
      (group.tasks || []).forEach((task) => {
        if (task?.id) next[task.id] = task.includedByDefault !== false;
      });
    });
    setChecked(next);
    const first = scopeGroups(row, nextTier)[0];
    setGroupId(first?.id || '');
  };

  const chooseService = (id) => {
    const row = services.find((item) => item.id === id) || null;
    setServiceId(id);
    setTierId('');
    setStep('choose');
    if (row && !tiersOf(row).length) seedChecks(row, null);
    else setChecked({});
  };

  const chooseTier = (id) => {
    setTierId(id);
    seedChecks(service, id ? tiers.find((row) => row.id === id) : null);
  };

  const openScope = (id) => {
    if (id !== tierId) chooseTier(id);
    setStep('scope');
  };

  const toggleTask = (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleGroup = (group) => {
    const ids = (group.tasks || []).map((task) => task.id).filter(Boolean);
    const allOn = ids.length > 0 && ids.every((id) => checked[id]);
    setChecked((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = !allOn;
      });
      return next;
    });
    setGroupId(group.id);
  };

  const confirm = async () => {
    if (!service) return;
    if (tiers.length && tierId === '' && step === 'choose') {
      toast.error('Select a tier, or task groups only');
      return;
    }
    const scopeTier = tierId ? tier : null;
    const scope = scopeGroups(service, scopeTier);
    const tasks = checkedTasks(scope, checked);
    if (scope.some((group) => (group.tasks || []).length) && !tasks.length) {
      toast.error('Tick at least one task to include');
      return;
    }
    const pick = buildPick(service, scopeTier, scope, checked);
    if (!persist) {
      onAdded(pick);
      return;
    }
    try {
      setSaving(true);
      const [bookingServiceId] = await saveBookingServicesForPicks(bookingId, [pick]);
      onAdded({ ...pick, bookingServiceId });
    } catch (err) {
      toast.error(err?.message || 'Could not add the service');
    } finally {
      setSaving(false);
    }
  };

  const ready = Boolean(service) && (tiers.length === 0 || tierId !== '' || step === 'scope');

  return (
    <div
      className={`qf-picker${step === 'scope' ? ' is-scope' : ''}`}
      role="dialog"
      aria-modal="true"
      style={zIndex ? { zIndex } : undefined}
    >
      <div>
        <header className="qf-picker-bar">
          <strong>{step === 'scope' ? (tier?.name || service?.service || 'Task groups') : 'Select Tier Services'}</strong>
          <button type="button" onClick={onClose} aria-label="Close">×</button>
        </header>

        {step === 'choose' ? (
          <div className="qf-picker-body">
            <label>
              Select service
              <select value={serviceId} onChange={(e) => chooseService(e.target.value)} disabled={loading}>
                <option value="">{loading ? 'Loading services…' : 'Choose a service'}</option>
                {services.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.service}{row.serviceId ? ` · ${row.serviceId}` : ''}
                  </option>
                ))}
              </select>
            </label>

            {service && tiers.length ? (
              <>
                <p className="qf-picker-label">Select service tier</p>
                <p className="qf-picker-note">
                  Selecting a tier loads its task groups. You can untick tasks after opening the scope. The service code is {service.serviceId || 'not set'}.
                </p>
                <div className="qf-tier-cards">
                  {tiers.map((row) => (
                    <article key={row.id} className={tierId === row.id ? 'is-on' : ''}>
                      <div>
                        <b>{row.name || 'Untitled tier'}</b>
                        <small>
                          {taskCount(scopeGroups(service, row))} tasks · {row.code || service.serviceId || 'No code'}
                        </small>
                        <button type="button" onClick={() => openScope(row.id)}>View scope</button>
                      </div>
                      <strong>{dollars(tierAmount(row))}<small>per visit</small></strong>
                      <input
                        type="radio"
                        name="qf-tier"
                        checked={tierId === row.id}
                        onChange={() => chooseTier(row.id)}
                        aria-label={row.name || 'Tier'}
                      />
                    </article>
                  ))}
                  {pool.length ? (
                    <article className={tierId === 'groups' ? 'is-on' : ''}>
                      <div>
                        <b>Task groups only</b>
                        <small>{taskCount(pool)} tasks · {service.serviceId || 'No code'} · no tier</small>
                        <button type="button" onClick={() => { chooseTier('groups'); setStep('scope'); }}>View scope</button>
                      </div>
                      <input
                        type="radio"
                        name="qf-tier"
                        checked={tierId === 'groups'}
                        onChange={() => chooseTier('groups')}
                        aria-label="Task groups only"
                      />
                    </article>
                  ) : null}
                </div>
              </>
            ) : null}

            {service && !tiers.length ? (
              <p className="qf-picker-note">
                {service.service} has no tier. Its task groups are used directly. Code {service.serviceId || 'not set'}.
                <button type="button" onClick={() => setStep('scope')}>View scope</button>
              </p>
            ) : null}
          </div>
        ) : (
          <div className="qf-scope">
            <p className="qf-scope-code">{code || 'No code'}</p>
            <div className="qf-scope-grid">
              <aside>
                <header>
                  <span>Task groups</span>
                  <strong>{dollars(price)}</strong>
                </header>
                {groups.map((group) => {
                  const ids = (group.tasks || []).map((task) => task.id).filter(Boolean);
                  const on = ids.filter((id) => checked[id]).length;
                  return (
                    <div key={group.id} className={`qf-scope-group${activeGroup?.id === group.id ? ' is-on' : ''}`}>
                      <input
                        type="checkbox"
                        checked={ids.length > 0 && on === ids.length}
                        onChange={() => toggleGroup(group)}
                        aria-label={group.name || 'Group'}
                      />
                      <button type="button" onClick={() => setGroupId(group.id)}>
                        <b>{group.name || 'Group'}</b>
                        <small>{ids.length} tasks</small>
                      </button>
                      <em>{dollars(group.price)}</em>
                    </div>
                  );
                })}
                {!groups.length ? <p>No task groups on this service yet.</p> : null}
              </aside>
              <section>
                <header>
                  <div>
                    <h2>{activeGroup?.name || 'Tasks'}</h2>
                    <span>
                      {(activeGroup?.tasks || []).filter((task) => checked[task.id]).length} selected
                      {usingTierPrice ? ' · tier price is used, task prices are not added on top' : ''}
                    </span>
                  </div>
                </header>
                {(activeGroup?.tasks || []).map((task) => (
                  <label key={task.id} className="qf-scope-task">
                    <input
                      type="checkbox"
                      checked={Boolean(checked[task.id])}
                      onChange={() => toggleTask(task.id)}
                    />
                    <span>
                      <b>{task.name || 'Task'}</b>
                      <small>{task.scope || 'No client scope'}</small>
                      {showInstructions && task.instructions ? <small>{task.instructions}</small> : null}
                    </span>
                    <em>{dollars((Number(task.price) || 0) * (Number(task.quantity) || 1))}</em>
                    <small>{task.includedByDefault === false ? 'Optional' : 'Included'}</small>
                  </label>
                ))}
              </section>
            </div>
          </div>
        )}

        <footer>
          <button type="button" onClick={onClose}>Cancel</button>
          <span />
          {step === 'scope' ? (
            <button type="button" className="qf-scope-back" onClick={() => setStep('choose')}>← Back</button>
          ) : null}
          <button type="button" className="is-primary" disabled={saving || !ready} onClick={confirm}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
}
